/*
https://www.w3.org/TR/wai-aria-practices-1.1/examples/treeview/treeview-2/treeview-2a.html
*/

import DN from "owp.dn";
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { OrgTree, getLongName } from "@gss/common/OrgTree";
import { Col } from "@gss/components/Col";
import { FormLabel } from "@gss/components/FormLabel";
import { Expand, Remove, Search, Unchecked } from "@gss/components/icons";
import { InputCheckbox } from "@gss/components/InputCheckbox";
import { InputText } from "@gss/components/InputText";
import { Label } from "@gss/components/Label";
import { Row } from "@gss/components/Row";
import { Select } from "@gss/components/Select";
import type { OrgStructure, OrgStructureOrg } from "@gss/types/measurementStat";
import type { Selected } from "../types";
import { ButtonHelp } from "../util/ButtonHelp";
import { ListBox } from "../util/ListBox";
import { ListTree } from "../util/ListTree";
import { useTranslation } from "../util/Translation";
import { ButtonClear } from "./ButtonClear";
import { ButtonMultiSelect } from "./ButtonMultiSelect";
import { ButtonSelectAll } from "./ButtonSelectAll";
import type { TreeNode, TreeNodeData } from "./filters.types";
import "./OrgFilter.css";
import {
    combineSelected,
    isSelected,
    makeSelected,
    OPTION_ALL,
    updateSelectedList,
} from "./FilterUtil";
import { MultiSelectionDesc } from "./MultiSelectionDesc";

const filterValue = "organisations";

interface SearchParameters {
    value: string;
    nodes: TreeNode[];
}

interface Props {
    orgStructures: OrgStructure[];
    orgStructure: OrgStructure;
    setOrgStructure: (os: OrgStructure) => void;
    isPublic: boolean;
    managerOrganistions: string[];
    selected: Selected[];
    addSelected: (filterValue: string, selection: Selected) => void;
    removeSelected: (filterValue: string, optionValue: string) => void;
    setSelected: (filterValue: string, selection: Selected[]) => void;
    setMultiSelectValue: (value: string | undefined) => void;
    multiSelectValue?: string;
}

export function OrgFilter({
    orgStructures,
    orgStructure,
    setOrgStructure,
    isPublic,
    managerOrganistions,
    selected,
    addSelected,
    removeSelected,
    setSelected,
    multiSelectValue,
    setMultiSelectValue,
}: Props): JSX.Element | null {
    const [root, setRoot] = useState<TreeNode>();
    const [multiSelectSelected, setMultiSelectSelected] = useState<Selected[]>(
        [],
    );
    const [parents, setParents] = useState<TreeNode[]>();
    const [node, setNode] = useState<TreeNode>();
    const [search, setSearch] = useState<SearchParameters>();
    const { translation, translateFilter } = useTranslation();
    const parentsRef = useRef<HTMLUListElement>(null);
    const nodesRef = useRef<HTMLUListElement>(null);
    const inMultiSelection = multiSelectValue === filterValue;
    const disabled = Boolean(multiSelectValue) && !inMultiSelection;
    const selectedList = inMultiSelection ? multiSelectSelected : selected;
    // Require at least 2 selectable orgs. root excluded.
    const enableMultiSelection =
        !isPublic && orgStructure.organisations.length > 2;
    const t = translation.filters;

    useEffect(() => {
        const tree = new OrgTree(
            orgStructure.organisations,
            (o) => o.dn,
            createNodeData,
        );
        const root = tree.root;

        // Root is the default 'all selected' option
        if (root.data == null) {
            throw new Error(`Root node is missing data`);
        }
        root.data.value = OPTION_ALL;
        setRoot(root);
        setNode(root);
        setParents([root]);
        if (search != null) {
            const nodes = getSearchNodes(root, search.value);
            setSearch({ ...search, nodes });
        }
    }, [orgStructure]);

    if (
        root == null ||
        node == null ||
        parents == null ||
        !showOrgFilter(orgStructures)
    ) {
        return null;
    }

    const selectAll = () => {
        const nodes = search?.nodes ?? node.children;
        const unselectedNodes = nodes.filter(
            (n) => !selectedList.some((s) => s.key === n.data?.value),
        );
        if (unselectedNodes.length > 0) {
            const selections = unselectedNodes.map((n) => makeSelected(n));
            if (inMultiSelection) {
                setMultiSelectSelected([...multiSelectSelected, ...selections]);
            } else {
                setSelected(filterValue, [...selected, ...selections]);
            }
        }
    };

    const clear = () => {
        if (inMultiSelection) {
            setMultiSelectSelected([]);
        } else {
            // Don't send in any organization rules when clearing
            setSelected(filterValue, getDefaultSelected(orgStructure, []));
        }
    };

    const onChange = (node: TreeNode | Selected, checked: boolean) => {
        if (inMultiSelection) {
            setMultiSelectSelected(
                updateSelectedList(multiSelectSelected, node, checked),
            );
        } else if (checked) {
            addSelected(filterValue, makeSelected(node));
        } else {
            const key = isSelected(node) ? node.key : (node.data?.value ?? "");
            removeSelected(filterValue, key);
        }
    };

    const expandParent = (node: TreeNode): undefined => {
        setNode(node);
        setSearch(undefined);
        nodesRef.current?.focus();
    };

    const expandNode = (node: TreeNode): undefined => {
        if (!parents.includes(node)) {
            const newParents = parents.filter((p) => node.dn.endsWith(p.dn));
            newParents.push(node);
            setParents(newParents);
        }
        setNode(node);
        setSearch(undefined);
    };

    const closeNode = () => {
        if (node === root) {
            parentsRef.current?.focus();
            return undefined;
        }
        const res = node;
        const i = parents.indexOf(node) - 1;
        expandNode(parents[i]);
        return res;
    };

    const onSelectNode = (node: TreeNode, key: string) => {
        if (key === " " && (!inMultiSelection || node !== root)) {
            const checked = !selectedList.some(
                (s) => s.key === node.data?.value,
            );
            onChange(node, checked);
        }
    };

    const getNodeHeader = (n: TreeNode): ComponentChildren => {
        const used = new Set<string>();
        const results: string[] = [];
        for (const child of n.children) {
            const header = child.data?.header;
            if (header != null && !used.has(header)) {
                used.add(header);
                results.push(translateFilter(header));
            }
        }
        return results.length > 0 ? results.join(", ") : <>&nbsp;</>;
    };

    const renderMultiSelect = () => {
        if (!enableMultiSelection) {
            return null;
        }
        return (
            <ButtonMultiSelect
                inMultiSelection={inMultiSelection}
                disabled={disabled}
                className="float-end"
                buttonText
                onClick={() => {
                    if (inMultiSelection) {
                        if (multiSelectSelected.length > 1) {
                            const selection =
                                combineSelected(multiSelectSelected);
                            if (
                                !selected.some((s) => s.key === selection.key)
                            ) {
                                addSelected(filterValue, selection);
                            }
                        }
                        setMultiSelectValue(undefined);
                    } else {
                        setMultiSelectSelected([]);
                        setMultiSelectValue(filterValue);
                    }
                }}
            />
        );
    };

    const renderSearchField = () => {
        return (
            <InputText
                small
                placeholder={t.organisationSearch}
                value={search?.value}
                onChangeOpt={(value) => {
                    if (value != null) {
                        const nodes = getSearchNodes(root, value);
                        setSearch({ value, nodes });
                    } else {
                        setSearch(undefined);
                    }
                }}
            />
        );
    };

    const renderOrgStructureSelection = () => {
        if (orgStructures.length < 2) {
            return null;
        }
        return (
            <Select
                small
                disabled={disabled || inMultiSelection}
                value={orgStructure.value}
                onChange={(value) => {
                    const os = orgStructures.find((o) => o.value === value);
                    if (os != null) {
                        setOrgStructure(os);
                        setSelected(
                            filterValue,
                            getDefaultSelected(os, managerOrganistions),
                        );
                    }
                }}
            >
                {orgStructures.map((o) => ({
                    value: o.value,
                    children: translateFilter(o.name),
                }))}
            </Select>
        );
    };

    const renderNode = (n: TreeNode) => {
        const checked = selectedList.some((s) => s.key === n.data?.value);
        const isCurrentParent =
            search == null && node === n && n.children.length > 0;
        return (
            <Label
                className={isCurrentParent ? "fw-bold" : undefined}
                title={n.nameLong}
                noTitleIcon
            >
                <InputCheckbox
                    checked={checked}
                    onChange={() => onChange(n, !checked)}
                    disabled={disabled || (n === root && inMultiSelection)}
                    tabIndex={-1}
                />
                {n.name}
            </Label>
        );
    };

    const renderParents = () => {
        return (
            <>
                <FormLabel id="org-header-latest">{t.latest}</FormLabel>
                {renderMultiSelect()}
                <ListTree
                    ref={parentsRef}
                    nodes={parents}
                    disabled={disabled}
                    render={renderNode}
                    onExpand={expandParent}
                    onSelect={onSelectNode}
                    aria-labelledby="org-header org-header-latest"
                />
            </>
        );
    };

    const renderNodes = () => {
        return (
            <>
                <FormLabel id="org-header-nodes">
                    {search != null ? (
                        <>
                            <Search /> {t.organisationSearchResult}
                        </>
                    ) : (
                        getNodeHeader(node)
                    )}
                </FormLabel>
                <ButtonSelectAll
                    className="float-end"
                    disabled={disabled}
                    onClick={selectAll}
                />
                <ListTree
                    ref={nodesRef}
                    nodes={search?.nodes ?? node.children}
                    disabled={disabled}
                    render={renderNode}
                    onExpand={expandNode}
                    onClose={closeNode}
                    onSelect={onSelectNode}
                    aria-labelledby="org-header org-header-nodes"
                />
            </>
        );
    };

    const renderSelected = () => {
        return (
            <>
                <FormLabel id="org-header-selected">{t.selected}</FormLabel>
                <ButtonClear
                    disabled={disabled}
                    onClick={clear}
                    className="float-end"
                    buttonText
                />
                <ListBox
                    className="org-filter-selected"
                    disabled={disabled}
                    aria-labelledby="org-header org-header-selected"
                    onSelect={(i, key) => {
                        if (key === "Enter") {
                            onChange(selected[i], false);
                        }
                    }}
                >
                    {selectedList.map((s) => {
                        const title = t.removeSelection(s.name);
                        return (
                            <button
                                key={s.key}
                                type="button"
                                disabled={disabled}
                                tabIndex={-1}
                                title={title}
                                aria-label={title}
                                onClick={() => onChange(s, false)}
                            >
                                <Remove aria-hidden /> {s.name}
                            </button>
                        );
                    })}
                </ListBox>
            </>
        );
    };

    const renderedDescription = () => {
        return (
            <>
                <div className="description">{t.organisationsDesc1}</div>
                <div>
                    <Unchecked aria-hidden /> {t.organisationsDesc2}
                </div>
                <div>
                    <Expand aria-hidden /> {t.organisationsDesc3}
                </div>
                <div>
                    <Remove aria-hidden /> {t.organisationsDesc4}
                </div>
                {enableMultiSelection && (
                    <div>
                        <MultiSelectionDesc />
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            <Row className="mb-1">
                <Col>
                    <FormLabel id="org-header">{t.organisations}</FormLabel>
                    <ButtonHelp
                        popover={renderedDescription}
                        disabled={disabled}
                        className="float-end"
                        placement="bottom"
                        buttonText
                    />
                </Col>
                <Col>{renderSearchField()}</Col>
                <Col>{renderOrgStructureSelection()}</Col>
            </Row>
            <Row className="org-filter mb-2">
                <Col>{renderParents()}</Col>
                <Col>{renderNodes()}</Col>
                <Col>{renderSelected()}</Col>
            </Row>
        </>
    );
}

function getDefaultSelected(
    orgStructure: OrgStructure,
    selectedOrganisationIds: string[],
    addRoot?: boolean,
): Selected[] {
    const result = [];

    if (selectedOrganisationIds.length > 0) {
        for (const orgId of selectedOrganisationIds) {
            const selections = orgId
                .split(",")
                .map((id) =>
                    orgStructure.organisations.find((o) => o.id === id),
                )
                .filter((o): o is OrgStructureOrg => o != null)
                .map((o) => createNode(o))
                .map((o) => makeSelected(o));

            if (selections.length === 1) {
                result.push(selections[0]);
            } else if (selections.length > 1) {
                result.push(combineSelected(selections));
            }
        }
    }

    if (
        addRoot ||
        selectedOrganisationIds.includes(OPTION_ALL) ||
        result.length === 0
    ) {
        const root = orgStructure.organisations.find(
            (o) => o.id === orgStructure.rootId,
        );
        if (root != null) {
            const rootNode = createNode(root);
            // Root is the default 'all selected' option
            rootNode.data.value = OPTION_ALL;
            result.unshift(makeSelected(rootNode));
        }
    }

    return result;
}

OrgFilter.value = filterValue;

OrgFilter.getDefaultSelected = getDefaultSelected;

// Only show filter if we have an org structure with more than one organisation.
function showOrgFilter(orgStructures: OrgStructure[]): boolean {
    for (const os of orgStructures) {
        if (os.organisations.length > 1) {
            return true;
        }
    }
    return false;
}

function getSearchNodes(root: TreeNode, value: string): TreeNode[] {
    const values = value
        .toLowerCase()
        .split(" ")
        .filter((v) => v !== "");
    const results: TreeNode[] = [];
    const parseNode = (node: TreeNode) => {
        const dn = node.dn.toLowerCase();
        if (values.every((v) => dn.includes(v))) {
            results.push(node);
        }
        for (const child of node.children) {
            parseNode(child);
        }
    };
    parseNode(root);
    return results;
}

function createNode(org: OrgStructureOrg): Required<TreeNode> {
    const dn = new DN(org.dn);
    const name = dn.rdns[dn.rdns.length - 1].value;
    const data = createNodeData(org);
    return {
        name,
        nameLong: getLongName(dn),
        dn: org.dn,
        data,
        children: [],
        childrenMap: {},
    };
}

function createNodeData(org: OrgStructureOrg): TreeNodeData {
    return {
        value: org.id,
        header: org.type,
    };
}
